import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

function useTablesWorkspaceState() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTableName, setSelectedTableName] = useState(searchParams.get("table") || null);
  const [contentTab, setContentTab] = useState(searchParams.get("content") === "schema" ? "schema" : "data");
  const [rightPanelTab, setRightPanelTab] = useState(searchParams.get("panel") === "ai" ? "ai" : "details");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(searchParams.get("panel") === "ai");
  const [selectedRow, setSelectedRow] = useState(null);
  const [isCreatingRow, setIsCreatingRow] = useState(false);

  useEffect(() => {
    const nextTable = searchParams.get("table") || null;
    const nextContent = searchParams.get("content") === "schema" ? "schema" : "data";
    const nextPanel = searchParams.get("panel") === "ai" ? "ai" : "details";
    setSelectedTableName(nextTable);
    setContentTab(nextContent);
    setRightPanelTab(nextPanel);
    if (searchParams.get("panel") === "ai") {
      setIsRightPanelOpen(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (selectedTableName) {
      nextParams.set("table", selectedTableName);
    } else {
      nextParams.delete("table");
    }
    if (contentTab === "schema") {
      nextParams.set("content", "schema");
    } else {
      nextParams.delete("content");
    }
    if (isRightPanelOpen && rightPanelTab === "ai") {
      nextParams.set("panel", "ai");
    } else {
      nextParams.delete("panel");
    }
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [contentTab, isRightPanelOpen, rightPanelTab, searchParams, selectedTableName, setSearchParams]);

  useEffect(() => {
    setSelectedRow(null);
    setIsCreatingRow(false);
  }, [selectedTableName]);

  const selectTable = useCallback((tableName) => {
    setSelectedTableName(tableName);
    setSelectedRow(null);
    setIsCreatingRow(false);
    setRightPanelTab("details");
    setIsRightPanelOpen(true);
  }, []);

  const clearTableSelection = useCallback(() => {
    setSelectedTableName(null);
    setSelectedRow(null);
    setIsCreatingRow(false);
  }, []);

  const openRowDetails = useCallback((row) => {
    setSelectedRow(row);
    setIsCreatingRow(false);
    setRightPanelTab("details");
    setIsRightPanelOpen(true);
  }, []);

  const openCreateRow = useCallback(() => {
    setSelectedRow(null);
    setIsCreatingRow(true);
    setRightPanelTab("details");
    setIsRightPanelOpen(true);
  }, []);

  const openTableDetails = useCallback(() => {
    setSelectedRow(null);
    setIsCreatingRow(false);
    setRightPanelTab("details");
    setIsRightPanelOpen(true);
  }, []);

  const openAI = useCallback(() => {
    setRightPanelTab("ai");
    setIsRightPanelOpen(true);
  }, []);

  const closeRightPanel = useCallback(() => {
    setIsRightPanelOpen(false);
  }, []);

  const editorMode = useMemo(() => {
    if (isCreatingRow) {
      return "create";
    }
    if (selectedRow) {
      return "edit";
    }
    return "table";
  }, [isCreatingRow, selectedRow]);

  return {
    selectedTableName,
    contentTab,
    setContentTab,
    rightPanelTab,
    setRightPanelTab,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    isRightPanelOpen,
    selectedRow,
    isCreatingRow,
    editorMode,
    selectTable,
    clearTableSelection,
    openRowDetails,
    openCreateRow,
    openTableDetails,
    openAI,
    closeRightPanel,
  };
}

export default useTablesWorkspaceState;
